export interface CanvasBlobEncodeSuccess {
  id: number;
  blob: Blob;
  time: number;
}

export interface CanvasBlobEncodeFailure {
  id: number;
  error: string;
}

export type CanvasBlobEncodeResponse = CanvasBlobEncodeSuccess | CanvasBlobEncodeFailure;
export type CanvasBlobEncodePriority = 'important' | 'best-effort';

export interface CanvasBlobEncodeOptions {
  /**
   * Worker 인코딩은 동시에 처리 중인 요청 1건과, 최대 3건까지의 대기 큐를 사용
   * `important` 요청은 가능한 한 보존: 대기 중인 가장 오래된 `best-effort` 요청을 제거하거나,
   *   큐에 중요 작업만 남아 있으면 메인 스레드 인코딩으로 폴백
   * `best-effort` 요청은 큐가 가득 찼으면 폴백하지 않고 드롭.
   */
  priority?: CanvasBlobEncodePriority;
}

type CanvasBlobEncodeResult = Omit<CanvasBlobEncodeSuccess, 'id'>;

interface EncodeBitmapMessage {
  id: number;
  bitmap: ImageBitmap;
  type: string;
}

interface WorkerEncodeRequest {
  id: number;
  bitmap: ImageBitmap | null;
  type: string;
  priority: CanvasBlobEncodePriority;
  isCancelled: boolean;
  resolve: (value: CanvasBlobEncodeResult) => void;
  reject: (reason?: unknown) => void;
}

type EnqueueWorkerRequestResult = { status: 'admitted' } | { status: 'rejected'; error: unknown };

const DEFAULT_ENCODE_PRIORITY: CanvasBlobEncodePriority = 'important';
const MAX_PENDING_WORKER_REQUESTS = 3;

export class CanvasBlobEncodeDroppedError extends Error {
  constructor(message = 'Canvas blob encode request dropped') {
    super(message);
  }
}

export const isCanvasBlobEncodeDroppedError = (
  error: unknown
): error is CanvasBlobEncodeDroppedError => {
  return error instanceof CanvasBlobEncodeDroppedError;
};

let worker: Worker | null = null;
let nextRequestId = 0;
let activeWorkerRequest: WorkerEncodeRequest | null = null;

const workerRequestQueue: WorkerEncodeRequest[] = [];

const closeBitmap = (bitmap: ImageBitmap) => {
  try {
    bitmap.close();
  } catch {
    // The bitmap may already be transferred to the worker.
  }
};

const rejectRequest = (request: WorkerEncodeRequest, error: unknown) => {
  request.isCancelled = true;
  if (request.bitmap) {
    closeBitmap(request.bitmap);
  }
  request.reject(error);
};

const removeQueuedRequest = (request: WorkerEncodeRequest) => {
  const requestIndex = workerRequestQueue.indexOf(request);
  if (requestIndex >= 0) {
    workerRequestQueue.splice(requestIndex, 1);
  }
};

const rejectAllWorkerRequests = (error: unknown) => {
  const entries = [...(activeWorkerRequest ? [activeWorkerRequest] : []), ...workerRequestQueue];

  activeWorkerRequest = null;
  workerRequestQueue.length = 0;

  entries.forEach((request) => {
    rejectRequest(request, error);
  });
};

const isWorkerEncodingSupported = () => {
  return (
    typeof Worker !== 'undefined' &&
    typeof createImageBitmap !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined' &&
    typeof OffscreenCanvas.prototype.convertToBlob === 'function'
  );
};

function dispatchNextWorkerRequest() {
  if (activeWorkerRequest || workerRequestQueue.length === 0 || !worker) {
    return;
  }

  const request = workerRequestQueue[0];
  if (!request?.bitmap) {
    return;
  }

  workerRequestQueue.shift();
  activeWorkerRequest = request;

  try {
    worker.postMessage(
      {
        id: request.id,
        bitmap: request.bitmap,
        type: request.type,
      } satisfies EncodeBitmapMessage,
      [request.bitmap]
    );
  } catch (error) {
    activeWorkerRequest = null;
    rejectRequest(request, error);
    dispatchNextWorkerRequest();
  }
}

function getWorker() {
  if (worker) {
    return worker;
  }

  const nextWorker = new Worker(
    new URL(
      /* webpackChunkName: "CanvasBlobEncoderWorker" */ 'src/utils/canvas/canvasBlobEncoder.worker',
      import.meta.url
    )
  );

  worker = nextWorker;

  nextWorker.onmessage = (event: MessageEvent<CanvasBlobEncodeResponse>) => {
    const response = event.data;
    if (!response) {
      return;
    }

    const pending = activeWorkerRequest;
    if (!pending || pending.id !== response.id) {
      return;
    }

    activeWorkerRequest = null;

    if ('error' in response) {
      pending.reject(new Error(response.error));
      dispatchNextWorkerRequest();
      return;
    }

    pending.resolve({
      blob: response.blob,
      time: response.time,
    });
    dispatchNextWorkerRequest();
  };

  nextWorker.onerror = () => {
    nextWorker.terminate();

    if (worker !== nextWorker) {
      return;
    }

    worker = null;
    rejectAllWorkerRequests(new Error('Canvas blob encoder worker failed'));
  };

  return worker;
}

const findOldestBestEffortRequestIndex = () =>
  workerRequestQueue.findIndex(({ priority }) => priority === 'best-effort');

const isWorkerRequestQueueFull = () => workerRequestQueue.length >= MAX_PENDING_WORKER_REQUESTS;

const enqueueWorkerRequest = (request: WorkerEncodeRequest): EnqueueWorkerRequestResult => {
  if (request.priority === 'best-effort') {
    if (isWorkerRequestQueueFull()) {
      return { status: 'rejected', error: new CanvasBlobEncodeDroppedError() };
    }
  } else {
    const bestEffortRequestIndex = findOldestBestEffortRequestIndex();

    if (bestEffortRequestIndex >= 0) {
      const [evictedRequest] = workerRequestQueue.splice(bestEffortRequestIndex, 1);
      if (evictedRequest) {
        rejectRequest(evictedRequest, new CanvasBlobEncodeDroppedError());
      }
    } else if (isWorkerRequestQueueFull()) {
      return { status: 'rejected', error: new Error('Canvas blob encoder queue is full') };
    }
  }

  try {
    getWorker();
  } catch (error) {
    return { status: 'rejected', error };
  }

  workerRequestQueue.push(request);
  dispatchNextWorkerRequest();
  return { status: 'admitted' };
};

const encodeCanvasOnMainThread = (
  canvas: HTMLCanvasElement,
  type: string
): Promise<CanvasBlobEncodeResult> => {
  return new Promise((resolve, reject) => {
    const start = performance.now();

    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to encode canvas to blob'));
        return;
      }

      resolve({
        blob,
        time: Math.round(performance.now() - start),
      });
    }, type);
  });
};

const encodeCanvasWithWorker = (
  canvas: HTMLCanvasElement,
  type: string,
  options: CanvasBlobEncodeOptions = {}
): Promise<CanvasBlobEncodeResult> => {
  const priority = options.priority ?? DEFAULT_ENCODE_PRIORITY;
  const requestId = ++nextRequestId;

  return new Promise((resolve, reject) => {
    const request: WorkerEncodeRequest = {
      id: requestId,
      bitmap: null,
      type,
      priority,
      isCancelled: false,
      resolve,
      reject,
    };

    const enqueueResult = enqueueWorkerRequest(request);

    if (enqueueResult.status === 'rejected') {
      reject(enqueueResult.error);
      return;
    }

    createImageBitmap(canvas).then(
      (bitmap) => {
        if (request.isCancelled) {
          closeBitmap(bitmap);
          return;
        }

        request.bitmap = bitmap;
        dispatchNextWorkerRequest();
      },
      (error) => {
        if (request.isCancelled) {
          return;
        }

        removeQueuedRequest(request);
        request.reject(error);
        dispatchNextWorkerRequest();
      }
    );
  });
};

export const encodeCanvasToBlob = async (
  canvas: HTMLCanvasElement,
  type = 'image/png',
  options: CanvasBlobEncodeOptions = {}
): Promise<CanvasBlobEncodeResult> => {
  if (!isWorkerEncodingSupported()) {
    return encodeCanvasOnMainThread(canvas, type);
  }

  try {
    return await encodeCanvasWithWorker(canvas, type, options);
  } catch (error) {
    if (isCanvasBlobEncodeDroppedError(error)) {
      throw error;
    }

    return encodeCanvasOnMainThread(canvas, type);
  }
};

export const encodeCanvasToPng = (
  canvas: HTMLCanvasElement,
  options: CanvasBlobEncodeOptions = {}
) => {
  return encodeCanvasToBlob(canvas, 'image/png', options);
};
